import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { brl, rows } from "@/lib/api";
import { Percent, DollarSign, ShoppingBag, Target, ScanBarcode, Receipt, CalendarDays } from "lucide-react";
import useFetch from "@/hooks/useFetch";
import { ProgressBar, KpiCard, KpiSkeleton, ErrorState } from "@/components/panel/ui";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import { Badge, Button, Card, DataTable, Field, PageHeader, Select, EYEBROW } from "@/components/ui-kit";

/** "2026-07" → "julho de 2026", que é como a pessoa fala do mês. */
const nomeDoMes = (ym) => {
  const [ano, mes] = ym.split("-");
  const d = new Date(Number(ano), Number(mes) - 1, 1);
  return d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
};

export default function Commissions() {
  /* `""` significa "mês corrente" e é o estado inicial de propósito: sem o
     parâmetro na URL, o servidor responde o mês de hoje e a lista de vendas
     NÃO é filtrada — que é o comportamento que a tela sempre teve. Escolher um
     mês no seletor é que passa a recortar os dois juntos. */
  const [mes, setMes] = useState("");
  const [sellerId, setSellerId] = useState("");
  const [equipe, setEquipe] = useState([]);

  const filtros = { ...(mes ? { month: mes } : {}), ...(sellerId ? { seller_id: sellerId } : {}) };

  const { data, loading, error, retry } = useFetch("/pos/commissions", filtros);
  // Os KPIs acima somam tudo no servidor; esta lista é só o histórico paginado.
  const { items: sales, total, page, lastPage, setPage, isLoading, isError, refetch } = usePaginatedList("/pos/sales/my", filtros);

  /* A equipe só é buscada para quem pode trocar de vendedor — o servidor diz
     isso em `can_view_others`, e é ele quem decide. Pedir a lista antes de saber
     daria 403 no console de todo vendedor comum. */
  const podeVerOutros = !!data?.can_view_others;
  useEffect(() => {
    if (!podeVerOutros || equipe.length) return;
    api.get("/admin/sellers").then((r) => setEquipe(rows(r))).catch(() => {});
  }, [podeVerOutros, equipe.length]);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  // A mesma tela vive em dois menus (panel/menus.js): "Balcão" no admin e
  // "Gestão" no vendedor. O eyebrow é metadado de navegação, então segue a rota
  // — senão o vendedor lê uma seção que não existe na barra lateral dele.
  const isAdmin = pathname.startsWith("/admin");
  const pdvPath = isAdmin ? "/admin/pdv" : "/vendedor";

  // A taxa saiu do título (era "(X% sobre vendas)" solto ao lado do h1) e virou
  // parte da descrição, onde cabe explicar de onde ela vem.
  const taxa = data ? ` — ${(data.rate * 100).toFixed(0)}% do valor de cada venda` : "";
  const outroVendedor = !!sellerId && data?.seller?.name;
  const periodo = data?.month ? nomeDoMes(data.month) : "";

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <PageHeader
        eyebrow={isAdmin ? "Balcão" : "Gestão"}
        /* O título diz de QUEM é a comissão. Com o admin olhando a equipe,
           "Minhas comissões" seria mentira na tela — e é a tela onde alguém
           confere valor a pagar. */
        title={outroVendedor ? `Comissões de ${data.seller.name}` : "Minhas comissões"}
        icon={Percent}
        description={outroVendedor
          ? `Vendas fechadas por ${data.seller.name} no balcão${taxa}.`
          : `Comissão das vendas que você fechou no balcão${taxa}. Só conta venda registrada no seu login: o que outro vendedor lançar não entra neste total.`}
      />

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <Field label="Mês" className="flex-1">
            <Select value={mes} onChange={(e) => { setMes(e.target.value); setPage(1); }} data-testid="commission-month-select">
              <option value="">Mês atual</option>
              {/* Só meses COM venda, vindos do servidor: um intervalo gerado
                  faria o vendedor rolar por meses vazios anteriores à admissão.
                  A comissão vai no rótulo para dar para comparar sem trocar. */}
              {(data?.months || []).map((m) => (
                <option key={m.month} value={m.month}>
                  {nomeDoMes(m.month)} — {brl(m.commission)}
                </option>
              ))}
            </Select>
          </Field>

          {podeVerOutros && (
            <Field label="Vendedor" className="flex-1">
              <Select value={sellerId} onChange={(e) => { setSellerId(e.target.value); setPage(1); }} data-testid="commission-seller-select">
                <option value="">Eu</option>
                {equipe.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </Select>
            </Field>
          )}
        </div>
      </Card>

      {error ? <ErrorState onRetry={retry} />
        : loading ? <div className="space-y-6"><KpiSkeleton count={2} /><KpiSkeleton /></div>
        : (
          <div className="space-y-6">
            <Card className="p-5" data-testid="commission-meta-card">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <span className={`${EYEBROW} flex items-center gap-2`}>
                  <Target size={15} className="text-brand-600" aria-hidden />
                  <span className="flex items-center gap-1.5">
                    Meta de <span className="normal-case font-normal text-slate-500">{periodo}</span>
                  </span>
                </span>
                <p className="text-sm text-slate-600">
                  <span className="font-mono font-semibold text-slate-900 tabular-nums">{brl(data.month_sold)}</span> de <span className="font-mono tabular-nums">{brl(data.meta_mensal)}</span>
                  {data.meta_pct >= 100 && <span className="ml-2 text-xs font-semibold text-emerald-600">Meta batida!</span>}
                </p>
              </div>
              <ProgressBar pct={data.meta_pct} testId="commission-meta-bar" />
            </Card>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard icon={CalendarDays} label={`Comissão de ${periodo}`} value={brl(data.month_commission)} cls="text-brand-700" testId="kpi-commission-month" />
              <KpiCard icon={Percent} label="Comissão total" value={brl(data.total_commission)} testId="kpi-commission-total" />
              <KpiCard icon={DollarSign} label="Total vendido" value={brl(data.total_sold)} testId="kpi-total-sold" />
              <KpiCard icon={ShoppingBag} label="Vendas" value={data.sales_count} testId="kpi-sales-count" />
            </div>
          </div>
        )}

      <DataTable
        rowTestId="commission-sale-row"
        caption="Histórico de vendas com comissão"
        rows={sales}
        rowKey={(s) => s.id}
        loading={isLoading}
        error={isError}
        onRetry={refetch}
        empty={{
          icon: Receipt,
          title: mes ? `Nenhuma venda em ${periodo}` : "Nenhuma venda registrada",
          /* Com um mês escolhido, "abra o PDV" é conselho errado: a venda não
             falta, ela é de outro período. O caminho é trocar o mês. */
          desc: mes
            ? "Escolha outro mês no seletor acima para ver o período com venda."
            : "A comissão só aparece aqui depois que a venda é fechada no PDV.",
          action: mes ? null : <Button icon={ScanBarcode} onClick={() => navigate(pdvPath)}>Abrir o PDV</Button>,
          testId: "commission-sales-empty",
        }}
        columns={[
          {
            key: "venda", header: "Venda", mono: true, className: "text-slate-900",
            // `(s) => {s.number}` (com chaves) devolvia undefined: a coluna do
            // número da venda vinha vazia — e é ela que titula o cartão no mobile.
            cell: (s) => s.number,
          },
          { key: "data", header: "Data", className: "text-slate-500", cell: (s) => new Date(s.created_at).toLocaleDateString("pt-BR") },
          { key: "cliente", header: "Cliente", className: "text-slate-600", cell: (s) => s.customer_name },
          {
            key: "pagamento", header: "Pagamento", className: "text-slate-600 capitalize",
            // A venda cancelada continua no histórico, mas sem comissão: sem esta
            // marca o vendedor só veria o total do mês encolher, sem saber por quê.
            cell: (s) => (s.status === "cancelada"
              ? <Badge tone="danger" dot>Cancelada</Badge>
              : (s.payment_method || "").replace("_", " ") || "—"),
          },
          {
            key: "total", header: "Total", align: "right", mono: true,
            cell: (s) => <span className={s.status === "cancelada" ? "text-slate-400 line-through" : ""}>{brl(s.total)}</span>,
          },
          {
            key: "comissao", header: "Comissão", align: "right", mono: true, className: "font-semibold text-brand-700",
            cell: (s) => (s.status === "cancelada" ? <span className="font-normal text-slate-400">—</span> : brl(s.commission)),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="vendas" />
    </div>
  );
}
